import { Intercom } from '@intercom/messenger-js-sdk';
import type React from 'react';
import { useLayoutEffect } from 'react';
import { INTERCOM_APP_ID } from '../../lib/constants.ts';
import { useAppState } from '../../lib/hooks/use-app-state.ts';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { getFullName } from '../../utils/name.ts';

type Props = {
    children: React.ReactNode | React.ReactNode[];
};

export const IntercomProvider: React.FC<Props> = ({ children }) => {
    const { platoUser } = useAppState();
    // re-renders on languageChanged, so a pick in the language picker re-runs the effect
    const { language } = useLocale();

    // the Intercom dep ultimately mutates the DOM so we use useLayoutEffect
    useLayoutEffect(() => {
        if (!INTERCOM_APP_ID) {
            return;
        }
        try {
            // the library automatically handles loading/updating existing instances
            Intercom({
                api_base: 'https://api-iam.intercom.io',
                app_id: INTERCOM_APP_ID,
                name: getFullName(platoUser.firstName, platoUser.lastName),
                email: platoUser.email,
                customer_id: platoUser.platoCustomer.id,
                customer_name: platoUser.platoCustomer.name,
                created_at: platoUser.createdAt.getTime() / 1000,
                hide_default_launcher: true,
                // without this the messenger follows the browser language, not the app's.
                // The bare language ('fr'), not the formatting locale ('fr-CH'):
                // Intercom only accepts codes from its own supported list.
                language_override: language,
            });
        } catch (error) {
            // don't crash the page if we can't load Intercom
            console.error(`Error loading Intercom`, error);
        }
    }, [platoUser, language]);

    return children;
};
